(function (window, document) {
  'use strict';

  const PRODUCTS_PER_PAGE = 250;
  const PREVIEW_PAGE = 60;
  const FETCH_RETRIES = 3;
  const RETRY_BACKOFF_MS = 800;
  const PAGE_DELAY_MS = 150;
  const INPUT_DEBOUNCE_MS = 200;

  const FIELD_OPTIONS = [
    { value: '', key: 'field_any' },
    { value: 'title', key: 'field_title' },
    { value: 'vendor', key: 'field_vendor' },
    { value: 'tag', key: 'field_tag' },
    { value: 'type', key: 'field_type' },
    { value: 'body', key: 'field_body' },
    { value: 'variants.title', key: 'field_variant_title' },
    { value: 'variants.sku', key: 'field_sku' },
    { value: 'variants.barcode', key: 'field_barcode' },
    { value: 'handle', key: 'field_handle' }
  ];

  const OPERATOR_OPTIONS = [
    { value: 'contains', key: 'operator_contains' },
    { value: 'exact', key: 'operator_exact' },
    { value: 'excludes', key: 'operator_excludes' },
    { value: 'excludes-exact', key: 'operator_excludes_exact' }
  ];

  const FIELD_KEYS = FIELD_OPTIONS.reduce((labels, option) => {
    labels[option.value] = option.key;
    return labels;
  }, {});

  function sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  async function fetchWithRetry(url) {
    let lastError;
    for (let attempt = 0; attempt < FETCH_RETRIES; attempt += 1) {
      try {
        const response = await fetch(url, { credentials: 'same-origin' });
        if (response.ok) return response;
        if (response.status === 403 || response.status === 429 || response.status >= 500) {
          lastError = new Error('HTTP ' + response.status);
          await sleep(RETRY_BACKOFF_MS * (attempt + 1));
          continue;
        }
        throw new Error('HTTP ' + response.status);
      } catch (error) {
        lastError = error;
        if (attempt === FETCH_RETRIES - 1) break;
        await sleep(RETRY_BACKOFF_MS * (attempt + 1));
      }
    }
    throw lastError || new Error('Request failed');
  }

  function waitForQueryLib() {
    return new Promise((resolve) => {
      if (window.CollectionSearchQuery) {
        resolve(window.CollectionSearchQuery);
        return;
      }
      const timer = window.setInterval(() => {
        if (!window.CollectionSearchQuery) return;
        window.clearInterval(timer);
        resolve(window.CollectionSearchQuery);
      }, 50);
    });
  }

  function escapeHtml(value) {
    if (value == null) return '';
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function escapeRegex(value) {
    return String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  function optionsMarkup(options, translate) {
    return options.map((option) => (
      '<option value="' + escapeHtml(option.value) + '">' + escapeHtml(translate(option.key)) + '</option>'
    )).join('');
  }

  function cleanValue(value) {
    return String(value || '').replace(/\s+/g, ' ').trim();
  }

  function quotedValue(value) {
    return '"' + value.replace(/\\/g, '\\\\').replace(/"/g, '\\"') + '"';
  }

  function needsQuotes(value) {
    return /\s|[()"]/.test(value) || /^(AND|OR|NOT)$/i.test(value);
  }

  function formatAtomicValue(value, forcePhrase) {
    const cleaned = cleanValue(value);
    if (!cleaned) return '';
    return forcePhrase || needsQuotes(cleaned) ? quotedValue(cleaned) : cleaned;
  }

  function parserMessages(items, unknownMessage) {
    if (!Array.isArray(items)) return [];
    return items.map((item) => {
      if (typeof item === 'string') return item;
      if (item && typeof item.message === 'string') return item.message;
      if (item && typeof item.code === 'string') return item.code;
      return String(item || unknownMessage);
    }).filter(Boolean);
  }

  function highlightTitle(title, parsed) {
    let html = escapeHtml(title);
    const clauses = parsed && Array.isArray(parsed.clauses) ? parsed.clauses : [];
    const needles = [];
    clauses.forEach((clause) => {
      if (!clause || clause.negate || clause.operator === 'NOT') return;
      if (clause.field && clause.field !== 'title') return;
      if (clause.value) needles.push(String(clause.value));
    });
    Array.from(new Set(needles)).forEach((needle) => {
      const expression = new RegExp('(' + escapeRegex(escapeHtml(needle)) + ')', 'gi');
      html = html.replace(expression, '<mark>$1</mark>');
    });
    return html;
  }

  class QueryBuilder {
    constructor(root) {
      this.root = root;
      this.collectionSelect = root.querySelector('[data-qb-collection]');
      this.scopeEl = root.querySelector('[data-qb-scope]');
      this.currentWrap = root.querySelector('[data-qb-current]');
      this.currentList = root.querySelector('[data-qb-current-list]');
      this.groupsEl = root.querySelector('[data-qb-groups]');
      this.groupJoin = root.querySelector('[data-qb-group-join]');
      this.addGroupBtn = root.querySelector('[data-qb-add-group]');
      this.importBtn = root.querySelector('[data-qb-import]');
      this.resetVisualBtn = root.querySelector('[data-qb-reset-visual]');
      this.visualStatus = root.querySelector('[data-qb-visual-status]');
      this.builderEl = root.querySelector('[data-qb-builder]');
      this.builderOp = root.querySelector('[data-qb-builder-op]');
      this.builderField = root.querySelector('[data-qb-builder-field]');
      this.builderValue = root.querySelector('[data-qb-builder-value]');
      this.builderAdd = root.querySelector('[data-qb-builder-add]');
      this.builderHint = root.querySelector('[data-qb-builder-hint]');
      this.typeaheadEl = root.querySelector('[data-qb-typeahead]');
      this.queryInput = root.querySelector('[data-qb-query]');
      this.validationEl = root.querySelector('[data-qb-validation]');
      this.interpretationEl = root.querySelector('[data-qb-interpretation]');
      this.urlQueryEl = root.querySelector('[data-qb-url-query]');
      this.copyBtn = root.querySelector('[data-qb-copy]');
      this.clearBtn = root.querySelector('[data-qb-clear]');
      this.statusEl = root.querySelector('[data-qb-status]');
      this.parsedEl = root.querySelector('[data-qb-parsed]');
      this.resultsEl = root.querySelector('[data-qb-results]');
      this.translations = this.readTranslations();
      this.products = [];
      this.currentHandle = '';
      this.isFetching = false;
      this.fetchToken = 0;
      this.previewLimit = PREVIEW_PAGE;
      this.inputTimer = null;
      this.visualTimer = null;
      this.typeaheadActive = -1;
      this.lastVisualQuery = '';
      this.resetVisualGroups(false);
      this.bind();
      this.updateBuilderMode();
      this.applyUrlParams();
    }

    readTranslations() {
      const element = this.root.querySelector('[data-qb-translations]');
      if (!element) return {};
      try {
        return JSON.parse(element.textContent || '{}');
      } catch (error) {
        return {};
      }
    }

    t(key, variables) {
      let message = this.translations[key] || key;
      Object.keys(variables || {}).forEach((name) => {
        message = message.split('[[' + name + ']]').join(String(variables[name]));
      });
      return message;
    }

    bind() {
      this.collectionSelect.addEventListener('change', () => {
        this.previewLimit = PREVIEW_PAGE;
        this.loadCollection(this.collectionSelect.value);
      });

      this.queryInput.addEventListener('input', () => {
        this.previewLimit = PREVIEW_PAGE;
        if (this.currentQueryString() !== this.lastVisualQuery) {
          this.setVisualStatus(this.t('raw_mode'), '');
        }
        window.clearTimeout(this.inputTimer);
        this.inputTimer = window.setTimeout(() => this.render(), INPUT_DEBOUNCE_MS);
      });

      this.copyBtn.addEventListener('click', () => this.copyQuery());

      this.clearBtn.addEventListener('click', () => {
        this.queryInput.value = '';
        this.lastVisualQuery = '';
        this.previewLimit = PREVIEW_PAGE;
        this.resetVisualGroups(false);
        this.setVisualStatus(this.t('cleared'), '');
        this.render();
        const firstValue = this.groupsEl.querySelector('[data-qb-row-value]');
        if (firstValue) firstValue.focus();
      });

      this.groupsEl.addEventListener('click', (event) => this.onGroupsClick(event));
      this.groupsEl.addEventListener('input', (event) => this.onGroupsInput(event));
      this.groupsEl.addEventListener('change', (event) => this.onGroupsInput(event));
      this.groupJoin.addEventListener('change', () => {
        this.renumberGroups();
        this.scheduleVisualBuild();
      });

      this.addGroupBtn.addEventListener('click', () => {
        this.createGroup({ rowJoin: 'OR', rows: [{ field: '', operator: 'contains', value: '' }] });
        this.renumberGroups();
        this.setVisualStatus(this.t('new_group'), '');
        const values = this.groupsEl.querySelectorAll('[data-qb-row-value]');
        if (values.length) values[values.length - 1].focus();
      });

      this.importBtn.addEventListener('click', () => this.importRawIntoGroups(false));

      this.resetVisualBtn.addEventListener('click', () => {
        this.lastVisualQuery = '';
        this.resetVisualGroups(false);
        this.setVisualStatus(this.t('reset_keeps_raw'), '');
        const firstValue = this.groupsEl.querySelector('[data-qb-row-value]');
        if (firstValue) firstValue.focus();
      });

      if (this.currentList) {
        this.currentList.addEventListener('click', (event) => {
          const button = event.target.closest('.qb-current-btn');
          if (button) this.applyCurrentButton(button);
        });
      }

      this.resultsEl.addEventListener('click', (event) => {
        const pinButton = event.target.closest('.qb-card-pin-btn');
        if (pinButton) {
          this.togglePin(pinButton.dataset.handle);
          return;
        }
        const moreButton = event.target.closest('[data-qb-load-more]');
        if (moreButton) {
          this.previewLimit += PREVIEW_PAGE;
          this.render();
        }
      });

      this.builderAdd.addEventListener('click', () => this.addDirective());
      this.builderValue.addEventListener('keydown', (event) => this.onDirectiveKeydown(event));
      this.builderValue.addEventListener('input', () => this.refreshTypeahead());
      this.builderValue.addEventListener('focus', () => this.refreshTypeahead());
      this.builderValue.addEventListener('blur', () => {
        window.setTimeout(() => this.hideTypeahead(), 150);
      });
      this.typeaheadEl.addEventListener('mousedown', (event) => {
        const item = event.target.closest('.qb-typeahead-item');
        if (!item) return;
        event.preventDefault();
        this.selectTypeahead(item.dataset.handle);
      });
      this.builderOp.addEventListener('change', () => this.updateBuilderMode());
    }

    applyUrlParams() {
      let params;
      try {
        params = new URLSearchParams(window.location.search);
      } catch (error) {
        this.render();
        return;
      }
      const collection = params.get('collection');
      const query = params.get('q');
      if (query != null) {
        this.queryInput.value = query;
        if (!this.importRawIntoGroups(true)) {
          this.setVisualStatus(this.t('linked_raw'), '');
        }
      }
      if (collection) {
        this.collectionSelect.value = collection;
        if (this.collectionSelect.value === collection) {
          this.loadCollection(collection);
          return;
        }
      }
      this.render();
    }

    createGroup(groupData) {
      const data = groupData || {};
      const fieldset = document.createElement('fieldset');
      fieldset.className = 'qb-rule-group';
      fieldset.setAttribute('data-qb-group', '');
      fieldset.innerHTML =
        '<legend>' +
          '<span class="qb-group-legend">' +
            '<span data-qb-group-number>Group</span>' +
            '<span>' + escapeHtml(this.t('rows_joined')) + '</span>' +
            '<select class="qb-input qb-input-compact qb-group-join" data-qb-row-join aria-label="' + escapeHtml(this.t('row_connector')) + '">' +
              '<option value="OR">OR</option>' +
              '<option value="AND">AND</option>' +
            '</select>' +
            '<button type="button" class="qb-btn qb-group-remove" data-qb-remove-group aria-label="' + escapeHtml(this.t('remove_group')) + '">' + escapeHtml(this.t('remove')) + '</button>' +
          '</span>' +
        '</legend>' +
        '<div class="qb-rule-rows" data-qb-rows></div>' +
        '<div class="qb-group-actions"><button type="button" class="qb-btn" data-qb-add-row>' + escapeHtml(this.t('add_join_row', { join: 'OR' })) + '</button></div>';
      this.groupsEl.appendChild(fieldset);
      fieldset.querySelector('[data-qb-row-join]').value = data.rowJoin === 'AND' ? 'AND' : 'OR';
      const rows = Array.isArray(data.rows) && data.rows.length
        ? data.rows
        : [{ field: '', operator: 'contains', value: '' }];
      rows.forEach((row) => this.createRow(fieldset, row));
      return fieldset;
    }

    createRow(group, rowData) {
      const row = document.createElement('div');
      row.className = 'qb-rule-row';
      row.setAttribute('data-qb-row', '');
      row.innerHTML =
        '<select class="qb-input qb-row-field" data-qb-row-field aria-label="' + escapeHtml(this.t('field')) + '">' + optionsMarkup(FIELD_OPTIONS, (key) => this.t(key)) + '</select>' +
        '<select class="qb-input qb-row-operator" data-qb-row-operator aria-label="' + escapeHtml(this.t('operator')) + '">' + optionsMarkup(OPERATOR_OPTIONS, (key) => this.t(key)) + '</select>' +
        '<input type="text" class="qb-input qb-row-value" data-qb-row-value aria-label="' + escapeHtml(this.t('value')) + '" autocomplete="off" placeholder="' + escapeHtml(this.t('search_value')) + '">' +
        '<button type="button" class="qb-btn qb-row-remove" data-qb-remove-row aria-label="' + escapeHtml(this.t('remove_row')) + '">×</button>';
      group.querySelector('[data-qb-rows]').appendChild(row);
      const data = rowData || {};
      const normalizedField = data.field === 'product_type' ? 'type' : (data.field || '');
      row.querySelector('[data-qb-row-field]').value = normalizedField;
      row.querySelector('[data-qb-row-operator]').value = data.operator || 'contains';
      row.querySelector('[data-qb-row-value]').value = data.value || '';
      return row;
    }

    resetVisualGroups(showStatus) {
      this.groupsEl.innerHTML = '';
      this.groupJoin.value = 'AND';
      this.createGroup({ rowJoin: 'OR', rows: [{ field: '', operator: 'contains', value: '' }] });
      this.renumberGroups();
      if (showStatus !== false) {
        this.setVisualStatus(this.t('reset_visual'), '');
      }
    }

    onGroupsClick(event) {
      const addRowButton = event.target.closest('[data-qb-add-row]');
      if (addRowButton) {
        const group = addRowButton.closest('[data-qb-group]');
        this.createRow(group, { field: '', operator: 'contains', value: '' });
        this.renumberGroups();
        this.setVisualStatus(this.t('new_row'), '');
        const values = group.querySelectorAll('[data-qb-row-value]');
        values[values.length - 1].focus();
        return;
      }

      const removeRowButton = event.target.closest('[data-qb-remove-row]');
      if (removeRowButton) {
        const group = removeRowButton.closest('[data-qb-group]');
        removeRowButton.closest('[data-qb-row]').remove();
        if (!group.querySelector('[data-qb-row]')) {
          this.createRow(group, { field: '', operator: 'contains', value: '' });
        }
        this.renumberGroups();
        this.buildFromVisual();
        return;
      }

      const removeGroupButton = event.target.closest('[data-qb-remove-group]');
      if (removeGroupButton) {
        removeGroupButton.closest('[data-qb-group]').remove();
        if (!this.groupsEl.querySelector('[data-qb-group]')) {
          this.createGroup({ rowJoin: 'OR', rows: [{ field: '', operator: 'contains', value: '' }] });
        }
        this.renumberGroups();
        this.buildFromVisual();
      }
    }

    onGroupsInput(event) {
      if (!event.target.closest('[data-qb-group]')) return;
      if (event.target.matches('[data-qb-row-join]')) this.renumberGroups();
      this.scheduleVisualBuild();
    }

    scheduleVisualBuild() {
      window.clearTimeout(this.visualTimer);
      this.visualTimer = window.setTimeout(() => this.buildFromVisual(), INPUT_DEBOUNCE_MS);
    }

    renumberGroups() {
      this.groupsEl.querySelectorAll('.qb-row-connector, .qb-group-connector').forEach((connector) => connector.remove());
      const groups = Array.from(this.groupsEl.querySelectorAll('[data-qb-group]'));
      groups.forEach((group, groupIndex) => {
        const rowJoin = group.querySelector('[data-qb-row-join]').value === 'AND' ? 'AND' : 'OR';
        group.querySelector('[data-qb-group-number]').textContent = this.t('group_number', { group: groupIndex + 1 });
        group.querySelector('[data-qb-add-row]').textContent = this.t('add_join_row', { join: rowJoin });
        const rows = Array.from(group.querySelectorAll('[data-qb-row]'));
        rows.forEach((row, rowIndex) => {
          const variables = { group: groupIndex + 1, row: rowIndex + 1 };
          row.querySelector('[data-qb-row-field]').setAttribute('aria-label', this.t('aria_field', variables));
          row.querySelector('[data-qb-row-operator]').setAttribute('aria-label', this.t('aria_operator', variables));
          row.querySelector('[data-qb-row-value]').setAttribute('aria-label', this.t('aria_value', variables));
          row.querySelector('[data-qb-remove-row]').setAttribute('aria-label', this.t('aria_remove_row', variables));
          if (rowIndex === 0) return;
          const connector = document.createElement('div');
          connector.className = 'qb-row-connector';
          connector.setAttribute('aria-hidden', 'true');
          connector.textContent = rowJoin;
          row.before(connector);
        });
        if (groupIndex === 0) return;
        const connector = document.createElement('div');
        connector.className = 'qb-group-connector';
        connector.setAttribute('aria-hidden', 'true');
        connector.textContent = this.groupJoin.value === 'OR' ? 'OR' : 'AND';
        group.before(connector);
      });
    }

    readVisualGroups() {
      const errors = [];
      const groups = [];
      const groupElements = Array.from(this.groupsEl.querySelectorAll('[data-qb-group]'));
      groupElements.forEach((groupElement, groupIndex) => {
        const rows = [];
        const rowElements = Array.from(groupElement.querySelectorAll('[data-qb-row]'));
        rowElements.forEach((rowElement, rowIndex) => {
          const input = rowElement.querySelector('[data-qb-row-value]');
          const value = cleanValue(input.value);
          const isBlank = value === '';
          input.setAttribute('aria-invalid', isBlank ? 'true' : 'false');
          if (isBlank) {
            errors.push(this.t('row_needs_value', { group: groupIndex + 1, row: rowIndex + 1 }));
            return;
          }
          rows.push({
            field: rowElement.querySelector('[data-qb-row-field]').value,
            operator: rowElement.querySelector('[data-qb-row-operator]').value,
            value: value
          });
        });
        if (!rowElements.length || !rows.length) {
          errors.push(this.t('group_needs_row', { group: groupIndex + 1 }));
        }
        groups.push({
          rowJoin: groupElement.querySelector('[data-qb-row-join]').value === 'AND' ? 'AND' : 'OR',
          rows: rows
        });
      });
      return { groups: groups, errors: Array.from(new Set(errors)) };
    }

    formatVisualRow(row) {
      const exact = row.operator === 'exact' || row.operator === 'excludes-exact';
      const excluded = row.operator === 'excludes' || row.operator === 'excludes-exact';
      const scope = row.field ? row.field + ':' : '';
      const value = cleanValue(row.value);
      if (!exact && /\s/.test(value)) {
        const atoms = value.split(/\s+/).filter(Boolean).map((word) => scope + formatAtomicValue(word, false));
        return '(' + atoms.map((atom) => (excluded ? '-' : '') + atom).join(' AND ') + ')';
      }
      const atom = scope + formatAtomicValue(value, exact);
      return (excluded ? '-' : '') + atom;
    }

    formatVisualFilter(groups) {
      const groupQueries = groups.map((group) => (
        '(' + group.rows.map((row) => this.formatVisualRow(row)).join(' ' + group.rowJoin + ' ') + ')'
      ));
      if (groupQueries.length === 1) return groupQueries[0];
      return '(' + groupQueries.join(' ' + (this.groupJoin.value === 'OR' ? 'OR' : 'AND') + ' ') + ')';
    }

    buildFromVisual() {
      const visual = this.readVisualGroups();
      if (visual.errors.length) {
        this.setVisualStatus(visual.errors.join(' '), 'error');
        return false;
      }
      const directives = this.directiveTokensFromQuery(this.currentQueryString());
      const filter = this.formatVisualFilter(visual.groups);
      const query = directives.concat(filter).join(' ').trim();
      this.queryInput.value = query;
      this.lastVisualQuery = query;
      this.previewLimit = PREVIEW_PAGE;
      this.setVisualStatus(this.t('synchronized'), 'success');
      this.render();
      return true;
    }

    directiveTokensFromQuery(query) {
      const lib = window.CollectionSearchQuery;
      if (!lib) return [];
      let parsed;
      try {
        parsed = lib.parseQuery(query || '');
      } catch (error) {
        return [];
      }
      const tokens = [];
      const pins = Array.isArray(parsed.pins) ? parsed.pins : [];
      const boosts = Array.isArray(parsed.boosts) ? parsed.boosts : [];
      pins.forEach((pin) => {
        const handle = typeof pin === 'string' ? pin : (pin && (pin.handle || pin.value));
        if (handle) tokens.push('pin:' + String(handle).toLowerCase());
      });
      boosts.forEach((boost) => {
        if (!boost) return;
        if (boost.ast && typeof lib.serializeAst === 'function') {
          const expression = lib.serializeAst(boost.ast);
          if (expression) tokens.push('^' + expression);
          return;
        }
        if (!boost.value) return;
        tokens.push('^' + (boost.field ? boost.field + ':' : '') + formatAtomicValue(boost.value, Boolean(boost.phrase)));
      });
      return Array.from(new Set(tokens));
    }

    importRawIntoGroups(silent) {
      const query = this.currentQueryString();
      if (!query) {
        if (!silent) this.setVisualStatus(this.t('import_empty'), 'error');
        return false;
      }
      const lib = window.CollectionSearchQuery;
      let parsed;
      try {
        parsed = lib.parseQuery(query);
      } catch (error) {
        if (!silent) this.setVisualStatus(this.t('parse_failed', { error: error.message }), 'error');
        return false;
      }
      const errors = parserMessages(parsed.errors, this.t('unknown_error'));
      if (errors.length) {
        if (!silent) this.setVisualStatus(this.t('fix_before_import', { errors: errors.join(' ') }), 'error');
        return false;
      }
      const compatible = this.compatibleGroupsFromAst(parsed.ast);
      if (!compatible) {
        if (!silent) {
          this.setVisualStatus(this.t('complex_raw'), 'error');
        }
        return false;
      }
      this.renderVisualGroups(compatible);
      this.lastVisualQuery = query;
      this.setVisualStatus(this.t('imported'), 'success');
      this.render();
      return true;
    }

    unwrapGroup(node) {
      let current = node;
      while (current && current.type === 'group') current = current.child;
      return current;
    }

    compatibleGroupsFromAst(ast) {
      const root = this.unwrapGroup(ast);
      if (!root) return null;
      let groupJoin = 'AND';
      let groupNodes;
      if (root.type === 'and') {
        groupNodes = Array.isArray(root.children) ? root.children : [];
      } else if (root.type === 'or') {
        const children = Array.isArray(root.children) ? root.children : [];
        const hasGroupedBranches = children.some((child) => {
          const unwrapped = this.unwrapGroup(child);
          return child && child.type === 'group' && unwrapped && (unwrapped.type === 'and' || unwrapped.type === 'or');
        });
        if (hasGroupedBranches) {
          groupJoin = 'OR';
          groupNodes = children;
        } else {
          groupNodes = [root];
        }
      } else {
        groupNodes = [root];
      }
      if (!groupNodes.length) return null;
      const groups = [];
      for (let groupIndex = 0; groupIndex < groupNodes.length; groupIndex += 1) {
        const groupNode = this.unwrapGroup(groupNodes[groupIndex]);
        if (!groupNode) return null;
        let rowJoin = 'OR';
        let rowNodes = [groupNode];
        if (groupNode.type === 'or' || groupNode.type === 'and') {
          rowJoin = groupNode.type.toUpperCase();
          rowNodes = Array.isArray(groupNode.children) ? groupNode.children : [];
        }
        if (!rowNodes.length) return null;
        const rows = [];
        for (let rowIndex = 0; rowIndex < rowNodes.length; rowIndex += 1) {
          const row = this.astNodeToRow(rowNodes[rowIndex]);
          if (!row) return null;
          rows.push(row);
        }
        groups.push({ rowJoin: rowJoin, rows: rows });
      }
      return { groupJoin: groupJoin, groups: groups };
    }

    astNodeToRow(node) {
      let current = this.unwrapGroup(node);
      let excluded = false;
      if (current && current.type === 'not') {
        excluded = true;
        current = this.unwrapGroup(current.child);
      }
      if (!current || current.type !== 'clause' || current.value == null) return null;
      let field = current.field || '';
      if (field === 'product_type') field = 'type';
      if (!Object.prototype.hasOwnProperty.call(FIELD_KEYS, field)) return null;
      const exact = Boolean(current.phrase);
      return {
        field: field,
        operator: excluded ? (exact ? 'excludes-exact' : 'excludes') : (exact ? 'exact' : 'contains'),
        value: String(current.value)
      };
    }

    renderVisualGroups(data) {
      this.groupsEl.innerHTML = '';
      this.groupJoin.value = data.groupJoin === 'OR' ? 'OR' : 'AND';
      data.groups.forEach((group) => this.createGroup(group));
      this.renumberGroups();
    }

    setVisualStatus(message, state) {
      this.visualStatus.textContent = message;
      this.visualStatus.classList.toggle('is-error', state === 'error');
      this.visualStatus.classList.toggle('is-success', state === 'success');
    }

    onDirectiveKeydown(event) {
      const visible = !this.typeaheadEl.hidden;
      if (visible && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
        event.preventDefault();
        const items = this.typeaheadEl.querySelectorAll('.qb-typeahead-item');
        if (!items.length) return;
        this.typeaheadActive += event.key === 'ArrowDown' ? 1 : -1;
        if (this.typeaheadActive < 0) this.typeaheadActive = items.length - 1;
        if (this.typeaheadActive >= items.length) this.typeaheadActive = 0;
        items.forEach((item, index) => {
          const active = index === this.typeaheadActive;
          item.classList.toggle('is-active', active);
          item.setAttribute('aria-selected', active ? 'true' : 'false');
        });
        this.builderValue.setAttribute('aria-activedescendant', items[this.typeaheadActive].id);
        items[this.typeaheadActive].scrollIntoView({ block: 'nearest' });
        return;
      }
      if (visible && event.key === 'Enter') {
        const active = this.typeaheadEl.querySelector('.qb-typeahead-item.is-active');
        if (active) {
          event.preventDefault();
          this.selectTypeahead(active.dataset.handle);
          return;
        }
      }
      if (visible && event.key === 'Escape') {
        event.preventDefault();
        this.hideTypeahead();
        return;
      }
      if (event.key === 'Enter') {
        event.preventDefault();
        this.addDirective();
      }
    }

    refreshTypeahead() {
      if (this.builderOp.value !== 'pin') {
        this.hideTypeahead();
        return;
      }
      const query = cleanValue(this.builderValue.value).toLowerCase();
      const matches = this.products.filter((product) => {
        if (!query) return true;
        return String(product.handle || '').toLowerCase().includes(query) || String(product.title || '').toLowerCase().includes(query);
      }).slice(0, 25);
      this.renderTypeahead(matches);
    }

    renderTypeahead(matches) {
      if (!this.products.length) {
        this.typeaheadEl.innerHTML = '<div class="qb-typeahead-empty" role="status">' + escapeHtml(this.t('select_products')) + '</div>';
      } else if (!matches.length) {
        this.typeaheadEl.innerHTML = '<div class="qb-typeahead-empty" role="status">' + escapeHtml(this.t('no_product_search')) + '</div>';
      } else {
        this.typeaheadEl.innerHTML = matches.map((product, index) => (
          '<div class="qb-typeahead-item" id="' + escapeHtml(this.typeaheadEl.id + '-option-' + index) + '" role="option" aria-selected="false" data-handle="' + escapeHtml(product.handle || '') + '">' +
            '<div class="qb-typeahead-title">' + escapeHtml(product.title || '') + '</div>' +
            '<div class="qb-typeahead-handle">' + escapeHtml(product.handle || '') + '</div>' +
          '</div>'
        )).join('');
      }
      this.typeaheadEl.hidden = false;
      this.typeaheadActive = -1;
      this.builderValue.setAttribute('aria-expanded', 'true');
      this.builderValue.removeAttribute('aria-activedescendant');
    }

    hideTypeahead() {
      this.typeaheadEl.hidden = true;
      this.typeaheadActive = -1;
      if (this.builderValue.getAttribute('role') === 'combobox') {
        this.builderValue.setAttribute('aria-expanded', 'false');
      } else {
        this.builderValue.removeAttribute('aria-expanded');
      }
      this.builderValue.removeAttribute('aria-activedescendant');
    }

    selectTypeahead(handle) {
      this.builderValue.value = handle;
      this.hideTypeahead();
      this.addDirective();
    }

    updateBuilderMode() {
      const operation = this.builderOp.value;
      const isPin = operation === 'pin';
      this.builderEl.classList.toggle('is-pin', isPin);
      if (isPin) {
        this.builderValue.setAttribute('role', 'combobox');
        this.builderValue.setAttribute('aria-autocomplete', 'list');
        this.builderValue.setAttribute('aria-controls', this.typeaheadEl.id);
        this.builderValue.setAttribute('aria-expanded', 'false');
        this.builderValue.placeholder = this.t('pin_placeholder');
        this.builderHint.textContent = this.t('pin_hint');
      } else if (operation === 'boost-phrase') {
        this.builderValue.removeAttribute('role');
        this.builderValue.removeAttribute('aria-autocomplete');
        this.builderValue.removeAttribute('aria-controls');
        this.builderValue.removeAttribute('aria-expanded');
        this.builderValue.removeAttribute('aria-activedescendant');
        this.builderValue.placeholder = this.t('boost_phrase_placeholder');
        this.builderHint.textContent = this.t('boost_phrase_hint');
      } else {
        this.builderValue.removeAttribute('role');
        this.builderValue.removeAttribute('aria-autocomplete');
        this.builderValue.removeAttribute('aria-controls');
        this.builderValue.removeAttribute('aria-expanded');
        this.builderValue.removeAttribute('aria-activedescendant');
        this.builderValue.placeholder = this.t('boost_placeholder');
        this.builderHint.textContent = this.t('boost_hint');
      }
      this.hideTypeahead();
    }

    addDirective() {
      const operation = this.builderOp.value;
      const value = cleanValue(this.builderValue.value);
      if (!value) {
        this.builderValue.focus();
        return;
      }
      const tokens = [];
      if (operation === 'pin') {
        const handle = value.replace(/\s+/g, '').replace(/"/g, '').toLowerCase();
        if (!handle) return;
        tokens.push('pin:' + handle);
      } else {
        const field = this.builderField.value;
        const scope = field ? field + ':' : '';
        if (operation === 'boost-phrase') {
          tokens.push('^' + scope + formatAtomicValue(value, true));
        } else {
          value.split(/\s+/).filter(Boolean).forEach((word) => tokens.push('^' + scope + formatAtomicValue(word, false)));
        }
      }
      const before = this.currentQueryString();
      const after = (before ? before + ' ' : '') + tokens.join(' ');
      this.queryInput.value = after;
      if (before === this.lastVisualQuery) this.lastVisualQuery = after;
      this.builderValue.value = '';
      this.previewLimit = PREVIEW_PAGE;
      this.render();
      this.builderValue.focus();
    }

    async loadCollection(handle) {
      const token = ++this.fetchToken;
      if (!handle) {
        this.products = [];
        this.currentHandle = '';
        this.isFetching = false;
        this.statusEl.textContent = this.t('select_collection');
        this.resultsEl.innerHTML = '';
        this.updateScope();
        this.loadCurrentButtons('');
        this.render();
        return;
      }
      this.currentHandle = handle;
      this.products = [];
      this.isFetching = true;
      this.statusEl.textContent = this.t('loading_products');
      this.resultsEl.innerHTML = '';
      this.updateScope();
      this.loadCurrentButtons(handle, token);
      try {
        let page = 1;
        while (true) {
          const url = '/collections/' + encodeURIComponent(handle) + '/products.json?limit=' + PRODUCTS_PER_PAGE + '&page=' + page;
          const response = await fetchWithRetry(url);
          if (token !== this.fetchToken) return;
          const data = await response.json();
          const products = data && Array.isArray(data.products) ? data.products : [];
          products.forEach((product) => {
            if (typeof window.CollectionSearchQuery.attachBodyText === 'function') {
              window.CollectionSearchQuery.attachBodyText(product);
            }
          });
          this.products = this.products.concat(products);
          this.updateScope();
          this.render();
          if (products.length < PRODUCTS_PER_PAGE) break;
          page += 1;
          await sleep(PAGE_DELAY_MS);
        }
      } catch (error) {
        if (token !== this.fetchToken) return;
        const message = error && error.message ? error.message : 'unknown';
        this.statusEl.textContent = /403/.test(message)
          ? this.t('rate_limited')
          : (/404/.test(message) ? this.t('collection_missing') : this.t('load_error', { error: message }));
        return;
      } finally {
        if (token === this.fetchToken) {
          this.isFetching = false;
          this.updateScope();
        }
      }
      this.render();
    }

    updateScope() {
      if (!this.currentHandle) {
        this.scopeEl.innerHTML = '<strong>' + escapeHtml(this.t('active_scope')) + '</strong> ' + escapeHtml(this.t('no_collection'));
        return;
      }
      const option = this.collectionSelect.options[this.collectionSelect.selectedIndex];
      const title = option ? option.textContent.replace(/\s+\(\d+\)\s*$/, '') : this.currentHandle;
      const progress = this.isFetching
        ? this.t('scope_loading', { count: this.products.length })
        : this.t('scope_loaded', { count: this.products.length });
      this.scopeEl.innerHTML = '<strong>' + escapeHtml(this.t('active_scope')) + '</strong> ' + escapeHtml(title) + ' — ' + escapeHtml(progress);
    }

    async loadCurrentButtons(handle, token) {
      if (!this.currentWrap) return;
      if (!handle) {
        this.currentWrap.hidden = true;
        this.currentList.innerHTML = '';
        return;
      }
      this.currentWrap.hidden = false;
      this.currentList.innerHTML = '<div class="qb-current-loading">' + escapeHtml(this.t('current_loading')) + '</div>';
      try {
        const response = await fetchWithRetry('/collections/' + encodeURIComponent(handle));
        if (token !== this.fetchToken) return;
        const html = await response.text();
        const page = new DOMParser().parseFromString(html, 'text/html');
        const seen = new Set();
        const buttons = [];
        page.querySelectorAll('.search-suggestion').forEach((element) => {
          const label = cleanValue(element.textContent);
          if (!label) return;
          const query = cleanValue(element.getAttribute('data-query') || label.toLowerCase());
          const key = label + '\u0000' + query;
          if (seen.has(key)) return;
          seen.add(key);
          buttons.push({ label: label, query: query });
        });
        this.renderCurrentButtons(buttons);
      } catch (error) {
        if (token !== this.fetchToken) return;
        this.currentList.innerHTML = '<div class="qb-current-empty">' + escapeHtml(this.t('current_failed')) + '</div>';
      }
    }

    renderCurrentButtons(buttons) {
      if (!buttons.length) {
        this.currentList.innerHTML = '<div class="qb-current-empty">' + escapeHtml(this.t('current_none')) + '</div>';
        return;
      }
      this.currentList.innerHTML = buttons.map((button) => (
        '<button type="button" class="qb-current-btn" data-query="' + escapeHtml(button.query) + '" title="' + escapeHtml(this.t('load_query')) + '">' +
          '<span class="qb-current-btn-label">' + escapeHtml(button.label) + '</span>' +
          '<span class="qb-current-btn-query">' + escapeHtml(button.query) + '</span>' +
        '</button>'
      )).join('');
      this.updateCurrentActive();
    }

    applyCurrentButton(button) {
      this.queryInput.value = button.getAttribute('data-query') || '';
      this.previewLimit = PREVIEW_PAGE;
      if (!this.importRawIntoGroups(true)) {
        this.lastVisualQuery = '';
        this.setVisualStatus(this.t('button_raw'), '');
        this.render();
      }
      this.queryInput.focus();
    }

    updateCurrentActive() {
      if (!this.currentList) return;
      const query = this.currentQueryString();
      this.currentList.querySelectorAll('.qb-current-btn').forEach((button) => {
        button.classList.toggle('is-active', Boolean(query) && (button.getAttribute('data-query') || '') === query);
      });
    }

    currentQueryString() {
      return String(this.queryInput.value || '').trim();
    }

    updateQueryMeta(query, parsed, errors, warnings, matchedCount) {
      const parameters = new URLSearchParams();
      parameters.set('q', query);
      this.urlQueryEl.textContent = '?' + parameters.toString();
      this.queryInput.setAttribute('aria-invalid', errors.length ? 'true' : 'false');
      this.validationEl.className = 'qb-validation';
      if (!query) {
        this.validationEl.textContent = this.t('no_query');
        this.copyBtn.disabled = true;
      } else if (errors.length) {
        this.validationEl.classList.add('is-error');
        const errorNoun = errors.length === 1 ? this.t('error_singular') : this.t('error_plural');
        this.validationEl.innerHTML = '<strong>' + escapeHtml(this.t('fix_errors', { count: errors.length, noun: errorNoun })) + '</strong><ul>' +
          errors.map((error) => '<li>' + escapeHtml(error) + '</li>').join('') + '</ul>';
        this.copyBtn.disabled = true;
      } else if (warnings.length) {
        this.validationEl.classList.add('is-warning');
        const warningNoun = warnings.length === 1 ? this.t('warning_singular') : this.t('warning_plural');
        this.validationEl.innerHTML = '<strong>' + escapeHtml(this.t('valid_warnings', { count: warnings.length, noun: warningNoun })) + '</strong><ul>' +
          warnings.map((warning) => '<li>' + escapeHtml(warning) + '</li>').join('') + '</ul>';
        this.copyBtn.disabled = false;
      } else if (this.currentHandle && !this.isFetching && matchedCount === 0) {
        this.validationEl.classList.add('is-warning');
        this.validationEl.textContent = this.t('zero_results');
        this.copyBtn.disabled = false;
      } else {
        this.validationEl.classList.add('is-valid');
        this.validationEl.textContent = this.t('query_valid');
        this.copyBtn.disabled = false;
      }
      this.interpretationEl.textContent = this.describeQuery(query, parsed);
    }

    describeQuery(query, parsed) {
      if (!query) return this.t('no_filters');
      if (parsed && typeof parsed.plainEnglish === 'string' && parsed.plainEnglish.trim()) {
        return parsed.plainEnglish.trim();
      }
      const lib = window.CollectionSearchQuery;
      if (lib && typeof lib.describeQuery === 'function') {
        try {
          const description = lib.describeQuery(parsed);
          if (typeof description === 'string' && description.trim()) return description.trim();
        } catch (error) {
          try {
            const description = lib.describeQuery(query);
            if (typeof description === 'string' && description.trim()) return description.trim();
          } catch (secondError) {
            return this.visualDescription(parsed, query);
          }
        }
      }
      return this.visualDescription(parsed, query);
    }

    visualDescription(parsed, query) {
      if (query === this.lastVisualQuery) {
        const visual = this.readVisualGroups();
        if (!visual.errors.length) {
          const descriptions = visual.groups.map((group, index) => {
            const rows = group.rows.map((row) => this.describeRow(row));
            return this.t('group_matches', {
              group: index + 1,
              amount: group.rowJoin === 'OR' ? this.t('any') : this.t('all'),
              rows: rows.join('; ')
            });
          });
          const introduction = descriptions.length > 1
            ? (this.groupJoin.value === 'OR' ? this.t('any_group') + ' ' : this.t('every_group') + ' ')
            : '';
          return introduction + descriptions.join(' ' + (this.groupJoin.value === 'OR' ? 'OR ' : 'AND '));
        }
      }
      const clauses = parsed && Array.isArray(parsed.clauses) ? parsed.clauses.length : 0;
      const pins = parsed && Array.isArray(parsed.pins) ? parsed.pins.length : 0;
      const boosts = parsed && Array.isArray(parsed.boosts) ? parsed.boosts.length : 0;
      const clauseDescription = clauses
        ? ' (' + clauses + ' ' + (clauses === 1 ? this.t('searchable_clause') : this.t('searchable_clauses')) + ')'
        : '';
      const pinDescription = pins
        ? '; ' + this.t('place_pins', { count: pins, noun: pins === 1 ? this.t('product_singular') : this.t('product_plural') })
        : '';
      const boostDescription = boosts
        ? '; ' + this.t('float_boosts', { count: boosts, noun: boosts === 1 ? this.t('term_singular') : this.t('term_plural') })
        : '';
      return this.t('apply_raw') + clauseDescription + pinDescription + boostDescription + '.';
    }

    describeRow(row) {
      const field = FIELD_KEYS[row.field] ? this.t(FIELD_KEYS[row.field]) : (row.field || this.t('field_any'));
      const verbs = {
        contains: this.t('verb_contains'),
        exact: this.t('verb_exact'),
        excludes: this.t('verb_excludes'),
        'excludes-exact': this.t('verb_excludes_exact')
      };
      return field + ' ' + (verbs[row.operator] || this.t('verb_contains')) + ' “' + row.value + '”';
    }

    render() {
      const lib = window.CollectionSearchQuery;
      if (!lib) return;
      const query = this.currentQueryString();
      this.updateCurrentActive();
      let parsed;
      let errors = [];
      try {
        parsed = lib.parseQuery(query);
        errors = parserMessages(parsed && parsed.errors, this.t('unknown_error'));
      } catch (error) {
        parsed = { clauses: [], pins: [], boosts: [], errors: [error.message] };
        errors = [error.message || this.t('parser_failed')];
      }
      const warnings = parserMessages(parsed && parsed.warnings, this.t('unknown_error'));
      const clauses = parsed && Array.isArray(parsed.clauses) ? parsed.clauses : [];
      const pins = parsed && Array.isArray(parsed.pins) ? parsed.pins : [];
      const boosts = parsed && Array.isArray(parsed.boosts) ? parsed.boosts : [];
      this.parsedEl.textContent = clauses.length || pins.length || boosts.length || errors.length
        ? '[' + this.t('parsed_summary', {
          clauses: clauses.length + ' ' + (clauses.length === 1 ? this.t('clause_singular') : this.t('clause_plural')),
          pins: pins.length + ' ' + (pins.length === 1 ? this.t('pin_singular') : this.t('pin_plural')),
          boosts: boosts.length + ' ' + (boosts.length === 1 ? this.t('boost_singular') : this.t('boost_plural')),
          invalid: errors.length ? this.t('invalid_count', { count: errors.length }) : ''
        }) + ']'
        : '';
      if (errors.length) {
        this.updateQueryMeta(query, parsed, errors, warnings, 0);
        this.statusEl.textContent = this.t('fix_preview');
        this.resultsEl.innerHTML = '';
        return;
      }
      let matched = [];
      try {
        matched = this.products.filter((product) => lib.evaluateQuery(parsed, product));
      } catch (error) {
        errors = [error.message || this.t('evaluation_failed')];
        this.updateQueryMeta(query, parsed, errors, warnings, 0);
        this.statusEl.textContent = this.t('fix_preview');
        this.resultsEl.innerHTML = '';
        return;
      }
      const ordered = typeof lib.applyPins === 'function' ? lib.applyPins(parsed, matched, this.products) : matched;
      const pinSet = new Set(pins.map((pin) => String(typeof pin === 'string' ? pin : (pin.handle || pin.value || '')).toLowerCase()));
      const matchedSet = new Set(matched.map((product) => String(product.handle || '').toLowerCase()));
      const boostSet = new Set();
      if (boosts.length && typeof lib.productFullText === 'function' && typeof lib.matchesBoost === 'function') {
        matched.forEach((product) => {
          const handle = String(product.handle || '').toLowerCase();
          if (pinSet.has(handle)) return;
          const fullText = lib.productFullText(product);
          if (boosts.some((boost) => lib.matchesBoost(product, boost, fullText))) boostSet.add(handle);
        });
      }
      const shown = Math.min(ordered.length, this.previewLimit);
      this.updateQueryMeta(query, parsed, errors, warnings, matched.length);
      if (!this.currentHandle) {
        this.statusEl.textContent = this.t('select_collection');
      } else if (this.isFetching) {
        this.statusEl.textContent = this.t('loading_matches', {
          count: matched.length,
          noun: matched.length === 1 ? this.t('match_singular') : this.t('match_plural')
        });
      } else {
        this.statusEl.textContent = this.t('result_status', {
          count: matched.length,
          noun: matched.length === 1 ? this.t('match_singular') : this.t('match_plural'),
          pins: pins.length ? this.t('pinned_count', { count: pins.length }) : '',
          boosts: boostSet.size ? this.t('boosted_count', { count: boostSet.size }) : '',
          shown: shown
        });
      }
      const visible = ordered.slice(0, shown);
      if (!visible.length) {
        this.resultsEl.innerHTML = this.currentHandle && !this.isFetching
          ? '<div class="qb-empty">' + escapeHtml(this.t('no_results')) + '</div>'
          : '';
        return;
      }
      const fragment = document.createDocumentFragment();
      visible.forEach((product) => {
        fragment.appendChild(this.buildCard(product, parsed, pinSet, matchedSet, boostSet));
      });
      if (ordered.length > shown) {
        const more = document.createElement('button');
        more.type = 'button';
        more.className = 'qb-load-more';
        more.setAttribute('data-qb-load-more', '');
        const remaining = ordered.length - shown;
        more.textContent = this.t('show_more', { next: Math.min(remaining, PREVIEW_PAGE), remaining: remaining });
        fragment.appendChild(more);
      }
      this.resultsEl.innerHTML = '';
      this.resultsEl.appendChild(fragment);
    }

    buildCard(product, parsed, pinSet, matchedSet, boostSet) {
      const card = document.createElement('div');
      card.setAttribute('role', 'listitem');
      const handle = String(product.handle || '').toLowerCase();
      const isPinned = pinSet.has(handle);
      const isBoosted = !isPinned && boostSet.has(handle);
      card.className = 'qb-card' + (isPinned || isBoosted ? ' is-pinned' : '');
      const image = product.images && product.images[0]
        ? product.images[0].src.replace(/\.([^\.]+)$/, '_300x.$1')
        : 'https://cdn.shopify.com/s/files/1/0533/2089/files/placeholder-images-image_large.png';
      const pinLabel = isPinned ? this.t('unpin') : this.t('pin_top');
      const onlyByPin = isPinned && !matchedSet.has(handle);
      const badge = isPinned
        ? '<span class="qb-card-pin-badge">' + escapeHtml(this.t('pinned')) + (onlyByPin ? escapeHtml(this.t('override')) : '') + '</span>'
        : (isBoosted ? '<span class="qb-card-pin-badge">' + escapeHtml(this.t('boosted')) + '</span>' : '');
      card.innerHTML =
        badge +
        '<a href="/products/' + encodeURIComponent(product.handle || '') + '" target="_blank" rel="noopener">' +
          '<img src="' + escapeHtml(image) + '" alt="' + escapeHtml(product.title || '') + '" loading="lazy">' +
        '</a>' +
        '<div class="qb-card-body">' +
          '<h4 class="qb-card-title"><a href="/products/' + encodeURIComponent(product.handle || '') + '" target="_blank" rel="noopener">' + highlightTitle(product.title || '', parsed) + '</a></h4>' +
          '<div class="qb-card-handle">' + escapeHtml(product.handle || '') + '</div>' +
          '<button type="button" class="qb-card-pin-btn" data-handle="' + escapeHtml(product.handle || '') + '">' + pinLabel + '</button>' +
        '</div>';
      return card;
    }

    togglePin(handle) {
      if (!handle) return;
      const lib = window.CollectionSearchQuery;
      const before = this.currentQueryString();
      let parsed;
      try {
        parsed = lib.parseQuery(before);
      } catch (error) {
        return;
      }
      const pins = Array.isArray(parsed.pins) ? parsed.pins : [];
      const normalized = handle.toLowerCase();
      const isPinned = pins.some((pin) => String(typeof pin === 'string' ? pin : (pin.handle || pin.value || '')).toLowerCase() === normalized);
      let after;
      if (isPinned) {
        const expression = new RegExp('(^|\\s)pin:' + escapeRegex(handle) + '(?=\\s|$)', 'gi');
        after = before.replace(expression, '$1').replace(/[ \t]{2,}/g, ' ').trim();
      } else {
        after = (before ? before + ' ' : '') + 'pin:' + handle;
      }
      this.queryInput.value = after;
      if (before === this.lastVisualQuery) this.lastVisualQuery = after;
      this.previewLimit = PREVIEW_PAGE;
      this.render();
    }

    async copyQuery() {
      const query = this.currentQueryString();
      if (!query || this.copyBtn.disabled) return;
      const original = this.copyBtn.textContent;
      try {
        await navigator.clipboard.writeText(query);
        this.copyBtn.textContent = this.t('copied');
      } catch (error) {
        const textarea = document.createElement('textarea');
        textarea.value = query;
        textarea.style.position = 'fixed';
        textarea.style.left = '-9999px';
        document.body.appendChild(textarea);
        textarea.select();
        try {
          document.execCommand('copy');
          this.copyBtn.textContent = this.t('copied');
        } catch (secondError) {
          this.copyBtn.textContent = this.t('copy_failed');
        }
        textarea.remove();
      }
      window.setTimeout(() => {
        this.copyBtn.textContent = original;
      }, 1500);
    }
  }

  async function init() {
    const root = document.querySelector('.qb-page');
    if (!root || root.dataset.initialized) return;
    root.dataset.initialized = 'true';
    await waitForQueryLib();
    new QueryBuilder(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})(window, document);
